// MACHINERY V2 (Stroje) — the profile tab CONTRACT (M2.4).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.4 + §5 "Catalog-vs-custom
// rule" + design_handoff_machinery_v3/README.md §3 / artboard 0504.
//
// >>> ONE PLACE DECIDES WHICH TABS EXIST. <<<
// Two consumers need that answer — MachineProfileV2 (which tab opens by
// default, and whether a remembered tab is still valid) and ProfileTabs (what
// to paint). Splitting it would be the exact "two places decide" defect the
// M2.1 card-state contract exists to prevent, one layer up.
//
// FILENAME: do NOT rename this back to `profileTabs.ts`. It would differ from
// `ProfileTabs.tsx` only in casing, which TypeScript rejects on a
// case-insensitive filesystem (TS1149) and which silently resolved the sibling
// component's import to THIS module (TS1192) before the rename.
//
// THE GATES (all are "never render a tab that is empty BY CONSTRUCTION"):
//   - Live          NEVER on a decommissioned machine (M2.4 review ruling D:
//                   a retired machine has no live surface at all — no
//                   telemetry fetch, no ribbon, no tab), and otherwise only
//                   when the machine HAS a gateway connection. The
//                   discriminator is the M2.1 card state: every state except
//                   `unconnected` means a MachineConnection row exists. We read
//                   the DERIVED state, we never re-test `telemetry.conn`.
//                   `awaiting` DOES get the tab: the agent is reachable and the
//                   ledger fills the moment it reports — that is empty NOW, not
//                   empty by construction. ("Unconnected machines simply have
//                   no Live tab" — integration-model artboard 0600, verbatim.)
//   - Špecifikácie  only when `catalogModelId` is set. A custom machine has no
//                   catalog spec sheet to show, ever.
// A custom, unconnected machine therefore lands on exactly three tabs.
//
// DEFAULT = Live when it exists, else Servisné správy (M2.4 acceptance). This
// is a DEVIATION from the live profile, whose default is always
// `serviceReports` (data package §1) — the designer's ruling, carried by the
// ticket.
//
// Tab BODIES are not built here: M2.5 owns SR / Dokumenty / Špecifikácie /
// Diely, M2.10 owns Live. See ProfileTabBodies.tsx.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { MachineryV2CardState } from "../fleet/cardStates";

/** The five profile surfaces, in artboard-0504 order. */
export type MachineryV2ProfileTab =
  | "live"
  | "serviceReports"
  | "documents"
  | "specifications"
  | "parts";

/** i18next keys (myMachinery namespace). v2 carries its OWN tab vocabulary —
 *  the designer labels the surfaces Dokumenty / Diely a materiál, where the
 *  live profile says Manuals & Software / Parts and Consumables. One block, so
 *  the wording cannot drift tab-to-tab. */
export const PROFILE_TAB_LABEL_KEYS: Record<MachineryV2ProfileTab, string> = {
  live: "myMachinery:v2.profile.tabs.live",
  serviceReports: "myMachinery:v2.profile.tabs.serviceReports",
  documents: "myMachinery:v2.profile.tabs.documents",
  specifications: "myMachinery:v2.profile.tabs.specifications",
  parts: "myMachinery:v2.profile.tabs.parts",
};

export interface ProfileTabContext {
  /** From deriveCardPresentation(telemetry).state — NOT raw telemetry. */
  state: MachineryV2CardState;
  /** `!!machine.catalogModelId` — the catalog-vs-custom discriminator. */
  hasCatalogModel: boolean;
  /**
   * `machine.isDecommissioned === true`. A retired machine loses the Live tab
   * OUTRIGHT (ruling D) rather than incidentally: MachineProfileV2 already
   * fetches it no telemetry, so `state` would derive `unconnected` anyway —
   * but "no live surface for a retired machine" is a RULE, and a rule enforced
   * only as a side effect of another rule breaks silently the first time
   * telemetry arrives from somewhere else.
   */
  decommissioned: boolean;
}

/** True when a MachineConnection row exists for this machine. */
export const hasLiveTab = (state: MachineryV2CardState): boolean =>
  state !== "unconnected";

/** The ordered tab set for one machine. */
export const buildProfileTabs = (ctx: ProfileTabContext): MachineryV2ProfileTab[] => {
  const tabs: MachineryV2ProfileTab[] = [];
  if (!ctx.decommissioned && hasLiveTab(ctx.state)) tabs.push("live");
  tabs.push("serviceReports", "documents");
  if (ctx.hasCatalogModel) tabs.push("specifications");
  tabs.push("parts");
  return tabs;
};

/** Live when present, else Servisné správy. */
export const defaultProfileTab = (
  tabs: MachineryV2ProfileTab[]
): MachineryV2ProfileTab => (tabs.indexOf("live") >= 0 ? "live" : "serviceReports");

/**
 * The tab actually rendered: the user's pick while it remains valid, else the
 * default.
 *
 * WHY THE FALLBACK IS NOT COSMETIC: telemetry arrives one tick AFTER the
 * machine does, so the first render of a connected machine has no Live tab yet.
 * Leaving `chosen` null until the user clicks lets the default RE-RESOLVE to
 * Live the moment the gateway answers, without ever overriding a deliberate
 * choice. The validity test also covers the reverse — a machine that drops off
 * the gateway while its Live tab is open falls back instead of rendering a tab
 * that no longer exists.
 */
export const resolveProfileTab = (
  tabs: MachineryV2ProfileTab[],
  chosen: MachineryV2ProfileTab | null
): MachineryV2ProfileTab =>
  chosen && tabs.indexOf(chosen) >= 0 ? chosen : defaultProfileTab(tabs);
